import type { DriveArtifactItem, DriveHiFiModel, DriveLifecycle, DriveSubdomainNode } from './types';
import type { DriveCanonicalState } from './driveCanonicalLanes';

export function lifeToCanonicalState(life: DriveLifecycle): DriveCanonicalState {
  switch (life) {
    case 'drafted':
      return 'defined';
    case 'active':
      return 'active';
    case 'closed':
      return 'posted';
    case 'created':
    default:
      return 'created';
  }
}

export function artifactsForSubdomain(
  artifacts: DriveArtifactItem[],
  subdomainId: string,
  grandchildIds: string[]
): DriveArtifactItem[] {
  const allowed = new Set([subdomainId, ...grandchildIds]);
  return artifacts.filter((art) => {
    const keys = [art.subdomainId, art.lane, art.filterScope].filter(Boolean) as string[];
    return keys.some((key) => allowed.has(key));
  });
}

export function artifactsForLane(
  laneId: string,
  subdomainId: string,
  artifacts: DriveArtifactItem[]
): DriveArtifactItem[] {
  return artifacts.filter(
    (art) =>
      art.lane === laneId ||
      art.filterScope === laneId ||
      (art.subdomainId === subdomainId && !art.lane && laneId === subdomainId)
  );
}

/** Scope drill view: grandchild lanes become flow lanes (same layout as overview). */
export function buildDrillScopedModel(
  subdomain: DriveSubdomainNode,
  artifacts: DriveArtifactItem[],
  domainLabel: string
): DriveHiFiModel {
  const grandchildren = subdomain.grandchildren ?? [];
  const blocked = artifacts.filter((a) => a.status === 'blocked').length;
  const movedRecently = artifacts.filter((a) => a.status === 'moved').length;
  const summary = {
    inFlow: artifacts.length,
    movedRecently,
    blocked,
    expectedToMove: artifacts.filter((a) => a.life === 'active').length,
  };

  if (grandchildren.length === 0) {
    return {
      domainLabel,
      subdomains: [{ id: subdomain.id, name: subdomain.name }],
      edges: [],
      artifacts,
      summary,
    };
  }

  return {
    domainLabel,
    subdomains: grandchildren.map((lane) => ({
      id: lane.id,
      name: lane.name,
      grandEdges: subdomain.grandEdges,
    })),
    edges: subdomain.grandEdges ?? [],
    artifacts,
    summary,
  };
}
